import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from 'formik';
import Select from 'react-select';
import { isBase64 } from 'is-base64';
import _ from "lodash";

import QuillEditor from "../../components/QuillEditor";
// import TiptapFullEditor from "../../components/TipTapEditor";
import callApi from "../callApi.js";
import { blogEntry } from "./data.js";


const BlogCopyReview = ({
    blogCopy,
    setBlogCopy,
    handleModalClose,
    setButtonDisabled,
    updateBlogCopyTable
}) => {
    let data = _.cloneDeep(blogEntry);
    if (blogCopy && blogCopy.blog_entry) {
        data = _.cloneDeep(blogCopy.blog_entry);
    }
    else if (blogCopy && blogCopy.outputs && blogCopy.outputs.sem) {
        data.title = blogCopy.outputs.sem.page_title;
        data.page_title = blogCopy.outputs.sem.page_title;
        data.url = 'blog/' + blogCopy.outputs.sem.url_slug;
        data.description = blogCopy.outputs.sem.meta_description;
        if (blogCopy.inputs && blogCopy.inputs.article_details) {
            data.category = blogCopy.inputs.article_details.category;
        }
        if (blogCopy.outputs.sem.image_alt_tags && blogCopy.outputs.sem.image_alt_tags.length > 0) {
            data.image_alt_text = blogCopy.outputs.sem.image_alt_tags[0];
        }
    }

    const [responseMessage, setResponseMessage] = useState(
        "There are no reviews to display"
    );
    const [pending, setPending] = useState(false);
    const [blogCopySignedURL, setBlogCopySignedURL] = useState("");
    const [blogCopyContent, setBlogCopyContent] = useState("");
    const [countries, setCountries] = useState([]);

    const blogCategories = [
        { value: 'about-us', label: 'About Us' },
        { value: 'credit-cards', label: 'Credit Cards' },
        { value: 'discounts', label: 'Discounts' },
        { value: 'personal-finance', label: 'Personal Finance' },
        { value: 'press-release', label: 'Press Release' },
        { value: 'value-of-data', label: 'Value Of Data' },
    ];

    const getCountries = () => {
        let url = `${process.env.REACT_APP_API_URL}/v1/core/country_codes`;
        callApi(url)
            .then((json) => {
                if (json.data && json.data.country_codes) {
                    let countries = [];
                    for (let country_code of json.data.country_codes) {
                        countries.push({
                            value: country_code,
                            label: country_code
                        });
                    }
                    setCountries(countries);
                }
            })
            .catch((err) => err);
    };

    const estimateReadDuration = (text) => {
        const plainText = text.replace(/<[^>]*>/g, ' ');
        const words = plainText.trim().split(/\s+/).filter(Boolean);
        const wordCount = words.length;
        const readingTimeMinutes = Math.ceil(wordCount / 200);
        return readingTimeMinutes <= 1
          ? '1 min read'
          : `${readingTimeMinutes} mins read`;
    }

    const getBlogCopySignedURL = () => {
        setPending(true);
        let url = `${process.env.REACT_APP_API_URL}/v1/content/blog/generated_signed_url`;
        callApi(url, 'POST', {'blog_signed_url': {'id': blogCopy.id, 'mode': 'read', 'file_name': 'blog_copy'}})
            .then(async (json) => {
                if (json.data && json.data.blog_signed_url) {
                    const signedUrl = json.data.blog_signed_url.signed_url
                    // Fetch file directly from GCS with CORS settings
                    const signedUrlResponse = await fetch(signedUrl, {
                        method: "GET",
                        mode: "cors",  // Enable cross-origin requests
                    });
                    if (!signedUrlResponse.ok) {
                        alert("Failed to fetch signed URL");
                    }
                    else {
                        const blogCopyContent = await signedUrlResponse.text();
                        data.read_duration = estimateReadDuration(blogCopyContent);
                        setBlogCopySignedURL(signedUrl);
                        setBlogCopyContent(blogCopyContent);
                    }
                }
                if (json.status && json.status.message) {
                    setResponseMessage(json.status.message);
                }
                setPending(false);
            })
            .catch((err) => err);
    };

    const editBlogCopySignedURL = (blogEntry, setSubmitting) => {
        setPending(true);
        let url = `${process.env.REACT_APP_API_URL}/v1/content/blog/generated_signed_url`;
        callApi(url, 'POST', {'blog_signed_url': {'id': blogCopy.id, 'mode': 'write', 'file_name': 'blog_copy'}})
            .then(async (json) => {
                if (json.data && json.data.blog_signed_url) {
                    const signedUrl = json.data.blog_signed_url.signed_url
                    // Fetch file directly from GCS with CORS settings
                    const signedUrlResponse = await fetch(signedUrl, {
                        method: "PUT",
                        mode: "cors",
                        headers: {
                            "Content-Type": "text/plain"
                        },
                        body: blogCopyContent
                    });
                    if (!signedUrlResponse.ok) {
                        alert("Failed to update file", signedUrlResponse.statusText);
                    }
                    else {
                        saveBlogEntry(blogEntry, setSubmitting);
                    }
                }
                if (json.status && json.status.message) {
                    setResponseMessage(json.status.message);
                }
                setPending(false);
            })
            .catch((err) => err);
    };

    const saveBlogEntry = (blogEntry, setSubmitting) => {
        let url = `${process.env.REACT_APP_API_URL}/v1/content/blog/generated_entry`;
        let data = {'blog_copy_id': blogCopy.id, 'blog_entry': blogEntry};
        callApi(url, 'POST', {'blog_copy_entry': data})
            .then((json) => {
                setSubmitting(false);
                setButtonDisabled(false);
                setBlogCopy({});
                handleModalClose();
                updateBlogCopyTable();
            })
            .catch((err) => err);
    };

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.readAsDataURL(file);
            fileReader.onload = () => {
                resolve(fileReader.result);
            };
            fileReader.onerror = (error) => {
                reject(error);
            };
        });
    };

    const handleIcon = async (e, setFieldValue, fieldName) => {
        const file = e.target.files[0];
        const base64 = await convertToBase64(file);
        setFieldValue(fieldName, base64);
    };

    useEffect(() => {
        if (countries.length === 0) {
            getCountries();
        }
    }, [countries]);

    useEffect(() => {
        getBlogCopySignedURL();
    }, []);

    return (
        <div className="index-page">
            <h1 className="title">
                Blog Copy - SEM Review
            </h1>
            <div>
                <Formik
                    initialValues={data}
                    validate={values => {
                        const errors = {};
                        const required_fields = [
                            'title', 'page_title', 'url', 'description', 'read_duration',
                            'category', 'image_url', 'image_alt_text'
                        ];
                        for (let value of required_fields) {
                            if (!_.get(values, value)) {
                                _.set(errors, value, 'Required');
                            }
                        }
                        if (values.countries.length > 0 && !values.countries[0]) {
                            errors.countries = 'Required';
                        }
                        if (values.image_url && 
                            !values.image_url.startsWith('https://') &&
                            !(isBase64(values.image_url, { allowMime: true }))) {
                            errors.image_url = 'Invalid URL'
                        }
                        return errors;
                    }}
                    onSubmit={(values, { setSubmitting }) => {
                        setSubmitting(true);
                        editBlogCopySignedURL(values, setSubmitting);
                    }}
                >
                    {({ values, isSubmitting, setFieldValue }) => (
                        <Form id="blog-copy-review">
                            <div className="pt-2">
                                <hr className="border-2 border-dark" />                        
                            </div>
                            <div className="row mt-40">
                                <label className="col-md-2">Title:*</label>
                                <Field 
                                    className="col-md-8" 
                                    type="text" name="title" />
                                <ErrorMessage 
                                    className="text-danger" 
                                    name="title" component="div" />
                            </div>
                            <div className="row mt-40">
                                <label className="col-md-2">Page Title:*</label>
                                <Field 
                                    className="col-md-8" 
                                    type="text" name="page_title" />
                                <ErrorMessage 
                                    className="text-danger" 
                                    name="page_title" component="div" />
                            </div>
                            <div className="row mt-40">
                                <label className="col-md-2">URL:*</label>
                                <Field 
                                    className="col-md-8" 
                                    type="text" name="url" />
                                <ErrorMessage 
                                    className="text-danger" 
                                    name="url" component="div" />
                            </div>
                            <div className="row mt-40">
                                <label className="col-md-2">Description:*</label>
                                <Field 
                                    className="col-md-8" 
                                    as="textarea"
                                    type="text" name="description" />
                                <ErrorMessage 
                                    className="text-danger" 
                                    name="description" component="div" />
                            </div>
                            <div className="row mt-40">
                                <label className="col-md-2">Read Duration:*</label>
                                <Field 
                                    className="col-md-8" 
                                    type="text" name="read_duration" />
                                <ErrorMessage 
                                    className="text-danger" 
                                    name="read_duration" component="div" />
                            </div>
                            <div className="row mt-40">
                                <label className="col-md-2">Category:*</label>
                                <Select
                                className="col-md-8"
                                name="category"
                                options={blogCategories}
                                defaultValue={
                                    { 
                                        value: values.category, 
                                        label: (function() {
                                            for (let blog_category of blogCategories) {
                                                if (blog_category.value === values.category) {
                                                    return blog_category.label
                                                } 
                                            }
                                            return values.category
                                        }())
                                    }
                                }
                                onChange={(selected) => setFieldValue("category", selected.value)} />
                                <ErrorMessage 
                                    className="text-danger" 
                                    name="category" component="div" />
                            </div>
                            <div className="row mt-40">
                                <label className="col-md-2">Countries:*</label>
                                <Select
                                    isMulti
                                    className="col-md-8"
                                    name="countries"
                                    options={countries}
                                    defaultValue={
                                        data.countries.map(country => {
                                            return {
                                                label: country,
                                                value: country
                                            };
                                        })
                                    }
                                    onChange={(selected) => {
                                        let countries = [];
                                        for (let item of selected) {
                                            countries.push(item.value);
                                        }
                                        setFieldValue("countries", countries);
                                    }} />
                                <ErrorMessage 
                                    className="text-danger" 
                                    name="country_code" component="div" />
                            </div>
                            <div className="row mt-40">
                                <label className="col-md-2">Image URL:*</label>
                                <Field 
                                    className="col-md-8"
                                    readOnly type="text" name="image_url" />
                                <div>
                                    (OR)
                                </div>
                                <Field
                                    className="col-md-8"
                                    name='image_url'>
                                    {({ form, field }) => {
                                        const { setFieldValue } = form
                                        return (
                                            <input
                                                type="file"
                                                accept="image/png, image/jpeg"
                                                className="col-md-8"
                                                onChange={(e) => handleIcon(e, setFieldValue, 'image_url')} />
                                        )
                                    }}
                                </Field>
                                <ErrorMessage 
                                    className="text-danger" 
                                    name="image_url" component="div" />
                            </div>
                            <div className="row mt-40">
                                <label className="col-md-2">Image Alt Text:*</label>
                                <Field 
                                    className="col-md-8" 
                                    as="textarea"
                                    type="text" name="image_alt_text" />
                                <ErrorMessage 
                                    className="text-danger" 
                                    name="image_alt_text" component="div" />
                            </div>
                        </Form>
                    )}
                </Formik>
                <div className="pt-4"></div>
            </div>
            <h1 className="title">
                Blog Copy - Content Review
            </h1>
            {
                pending ?
                    <div className="loading">
                        Loading...
                    </div> :
                    blogCopySignedURL && blogCopyContent ?
                        <div>
                            <div className="pt-2">
                                <hr className="border-2 border-dark" />                        
                            </div>
                            {/* Quill.js Editor */}
                            <QuillEditor value={blogCopyContent} onChange={setBlogCopyContent} />
                            {/* TipTap Editor */}
                            {/* <TiptapFullEditor value={blogCopyContent} onChange={setBlogCopyContent} /> */}
                        </div> :
                        <div className="loading">
                            {responseMessage}
                        </div>
            }
        </div>
    );
};


export default BlogCopyReview;